<script setup lang="ts">

import {onClickOutside} from '@vueuse/core'
import {Loader2Icon} from "lucide-vue-next";
import {computed, type Ref, ref} from "vue";
import {label} from "../../mixins/translations";
import {useSearchStore} from "../../stores/search";

interface Props {
    types?: string[],
    lang: string,
}

const props = withDefaults<Props>(defineProps<Props>(), {
    types: [],
    lang: 'NL',
});

const searchStore = useSearchStore();
const selectedOption : Ref<string> = ref('all');
const searchQuery : Ref<string> = ref('');
const openResults : Ref<boolean> = ref(false);

const searchUrl = computed((): string => {
    if(props.lang !== 'NL') {
        return `/en/search-results/${searchQuery.value.toLowerCase()}/`;
    }
    else {
        return `/zoekresultaten/${searchQuery.value.toLowerCase()}/`;
    }
})

const navigate = (type) => {
    if(type !== ''){
        searchStore.toggleSearch = false;
        window.location.href = `${searchUrl.value}?show-all=1#${type}`;
    }
    if(searchQuery.value.length) {
        window.location.href = searchUrl.value;
    }
}

let previewTimeout = null;
const preview = () => {
    clearTimeout(previewTimeout);

    previewTimeout = setTimeout(async () => {
        searchStore.getSearchResults(
            searchQuery.value,
            selectedOption.value
        );

        openResults.value = true;
    }, 400);
};

const target = ref(null);
onClickOutside(target, event => {openResults.value = false;})
</script>

<template>
    <div class="w-full relative pt-3 pb-3 mx-auto text-secondary-600 bg-gray-100 z-20" id="mainMenuSearch" v-show="searchStore.toggleSearch">
        <div class="container-header px-4 relative mx-auto">
            <div class="relative w-full max-w-full xl:max-w-lg 2xl:max-w-2xl bg-white flex items-center border border-gray-200 rounded-lg focus-within:shadow-lg mx-auto">
                <input
                    class="appearance-none outline-hidden focus:border-gray-300 p-4 border-0 focus:ring-0 border-gray-300 bg-transparent text-gray-600 font-normal text-sm w-9/12"
                    type="text" v-model="searchQuery" :placeholder="label('site.search.placeholder')" @keyup.enter="navigate" @keyup="preview">
                <div class="ml-auto px-4 text-gray-600">
                    <svg v-if="!searchStore.isLoading" xmlns="http://www.w3.org/2000/svg" class="size-5" fill="none" @click="navigate"
                         viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                    </svg>
                    <Loader2Icon v-else class="size-5 animate-spin" />
                </div>

                <!--SEARCHRESULTS-->
                <div class="w-full absolute top-16 bg-gray-50 shadow-xl rounded-xl max-h-[600px] overflow-y-auto" v-show="openResults" ref="target">
                    <div v-for="(results, key) in searchStore.results">
                        <div v-if="results.count">
                            <div v-if="key === 'products'" class="w-full py-2 px-6 bg-gray-100">
                                <h3 class="w-full text-lg font-bold tracking-tight text-gray-900" v-text="label('site.search.'+key)"></h3>
                                <nav class="flex flex-col mt-2 space-y-2 text-sm text-gray-500">
                                    <div v-for="result in results.items">
                                        <a :href="`/${result.full_slug}/`" class="w-full flex bg-white rounded-xl overflow-hidden mt-1" >
                                            <div class="w-full px-4 py-2 flex flex-row group">
                                                <div class="w-full text-base tracking-tight text-gray-900 group-hover:text-primary" v-text="result.name"></div>
                                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-5 w-5 lucide lucide-arrow-right-icon lucide-arrow-right transform transition-transform duration-500 ease-out hidden group-hover:flex text-primary mt-1"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                                            </div>
                                        </a>
                                    </div>
                                    <a href="javascript:" @click="navigate('products')" class="mt-1 inline-flex text-sm items-center gap-2 font-extrabold text-primary antialiased group">
                                        {{ label('site.search.show-results', {'count': results.count}) }}
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-5 w-5 lucide lucide-arrow-right-icon lucide-arrow-right transform transition-transform duration-500 ease-out group-hover:translate-x-1"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                                    </a>
                                </nav>
                            </div>
                            <div v-else-if="key === 'pages'" class="w-full py-2 px-6 bg-gray-100">
                                <h3 class="w-full text-lg font-bold tracking-tight text-gray-900" v-text="label('site.search.'+key)"></h3>
                                <nav class="flex flex-col mt-2 space-y-2 text-sm text-gray-500">
                                    <div v-for="result in results.items">
                                        <a :href="`${result.full_slug}`" class="w-full flex bg-white rounded-xl overflow-hidden mt-1" >
                                            <div class="w-full px-4 py-2 flex flex-row group">
                                                <div class="w-full text-base tracking-tight text-gray-900 group-hover:text-primary" v-text="result.title"></div>
                                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-5 w-5 lucide lucide-arrow-right-icon lucide-arrow-right transform transition-transform duration-500 ease-out hidden group-hover:flex text-primary mt-1"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                                            </div>
                                        </a>
                                    </div>
                                    <a href="javascript:" @click="navigate('pages')" class="mt-1 inline-flex text-sm items-center gap-2 font-extrabold text-primary antialiased group">
                                        {{ label('site.search.show-results', {'count': results.count}) }}
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-5 w-5 lucide lucide-arrow-right-icon lucide-arrow-right transform transition-transform duration-500 ease-out group-hover:translate-x-1"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                                    </a>
                                </nav>
                            </div>
                            <div v-else class="w-full py-3 px-6 bg-gray-100">
                                <h3 class="w-full text-lg font-bold tracking-tight text-gray-900" v-text="label('site.search.'+key)"></h3>
                                <nav class="flex flex-col mt-2 space-y-2 text-sm text-gray-500">
                                    <div v-for="result in results.items">
                                        <a :href="`/${result.full_slug}/`" class="w-full flex bg-white rounded-xl overflow-hidden mt-1" >
                                            <div class="w-full px-4 py-2 flex flex-row group">
                                                <div class="w-full text-base tracking-tight text-gray-900 hover:text-primary" v-text="result.title ?? result.question"></div>
                                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-5 w-5 lucide lucide-arrow-right-icon lucide-arrow-right transform transition-transform duration-500 ease-out hidden group-hover:flex text-primary mt-1"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                                            </div>
                                        </a>
                                    </div>
                                    <a href="javascript:" @click="navigate('articles')" class="mt-1 inline-flex text-sm items-center gap-2 font-extrabold text-primary antialiased group">
                                        {{ label('site.search.show-results', {'count': results.count}) }}
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-5 w-5 lucide lucide-arrow-right-icon lucide-arrow-right transform transition-transform duration-500 ease-out group-hover:translate-x-1"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                                    </a>
                                </nav>
                            </div>
                        </div>
                    </div>
                    <div class="w-full py-4 px-6 flex" v-if="searchStore.totalResults">
                        <a :href="searchUrl" class="bg-primary hover:bg-primary-400 cursur-pointer text-center text-white font-semibold px-6 py-3 rounded-md w-full whitespace-nowrap" v-html="label('site.search.show-all-results', {'count': searchStore.totalResults})"></a>
                    </div>
                    <div v-else class="w-full py-4 px-6 flex text-gray-600">
                        <div v-text="label('site.search.no-results')"></div>
                    </div>
                </div>
            </div>

            <span @click="searchStore.toggleSearch = false;" class="hidden lg:block absolute flex flex-row top-2 right-4 px-2 py-2 border border-transparent text-base text-black -mt-1 hover:bg-gray-200 cursor-pointer">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" class="h-6 w-6 rotate-45 float-left mt-0.5">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                </svg>
                <span class="float-left ml-1 flex">{{ label('site.search.close') }}</span>
            </span>
        </div>
    </div>
</template>
